
import { Injectable } from '@angular/core';
import { Device, DeviceInfo } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { GlobalConstants } from '../constant/constant';
import { Network } from '@capacitor/network';
import { AppInfoService } from './app-info.service';
import { Platform } from '@ionic/angular';
import { DeviceService, DeviceRegisterV3Response } from '@platform/api-client';

@Injectable({
  providedIn: 'root',
})
export class DeviceInfoService {
  deviceInfo?: DeviceInfo;
  availability: 'pending' | 'available' | 'failed' = 'pending';
  readonly ready = Device.getInfo().then(info => {
    this.deviceInfo = info;
    this.availability = 'available';
  }, () => { this.availability = 'failed'; });

  constructor(
    private platform: Platform,
    private appInfo: AppInfoService,
    private deviceService: DeviceService
  ) {
  }

  async getPlatform(): Promise<"ios" | "android" | "web"> {
    return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
  }

  async getOS(): Promise<string> {
    await this.ready;
    return this.deviceInfo?.operatingSystem;
  }

  async getOSMajorVersion(): Promise<number> {
    await this.ready;
    return +(this.deviceInfo?.osVersion?.split('.')[0]);
  }

  getBrowserName(): string {
    const agent = window.navigator.userAgent.toLowerCase();
    switch (true) {
      case /edge|edg\//.test(agent):
        return 'edge';
      case agent.indexOf('chrome') > -1 && !!(<any>window).chrome:
        return 'chrome';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }
  }

  async getAppVersion(): Promise<string> {
    await this.appInfo.ready;
    return this.appInfo.version;
  }

  async getInputType(): Promise<string> {
    await this.appInfo.ready;
    return this.inputType;
  }

  get inputType(): string | undefined {
    if (!this.appInfo.version) { return undefined; }
    const platform = Capacitor.getPlatform();
    const source = platform === 'web' ? this.getBrowserName() : platform;
    return GlobalConstants.INPUT_TYPE_PATTERN(source, this.appInfo.version);
  }

  /**
    * Checks if the current device is a mobile device.
    *
    * Returns true if:
    *  - The platform is not desktop AND
    *  - The platform is iOS or Android OR
    *  - The user agent matches common mobile devices and the platform is not a tablet
    *
    * @returns true if running on a mobile device (native or mobile browser), false otherwise
    */
  checkIsMobile(): boolean {
    const p = this.platform;
    const platforms = p.platforms();

    const isMobile = Boolean(
      /Android|webOS|iPhone|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
      !platforms.includes('tablet')
    );

    return !p.is('desktop') && (p.is('ios') || p.is('android')  || isMobile
    );
  }

  checkIsMobileWeb(): boolean {
    return this.platform.is("mobileweb");
  }

  async getNetworkStatus() {
    const status = await Network.getStatus();
    return status;
  }

  /**
   * Register or retrieve the device's public 4-character identifier for QR authentication.
   *
   * @param existingDeviceId - Optional. If provided, retrieves the existing device.
   *                          If omitted, registers new device using AccessToken.
   */
  async getDeviceId(existingDeviceId?: string): Promise<DeviceRegisterV3Response> {
    const body = existingDeviceId ? { deviceId: existingDeviceId } : {};
    const response = await this.deviceService.apiV3DevicePost(body).toPromise();
    return response;
  }

  async openAppStore() {
    if (this.platform.is('ios')) {
      window.open(GlobalConstants.STORE_URLS.IOS, '_blank');
    } else if (this.platform.is('android')) {
      window.open(GlobalConstants.STORE_URLS.ANDROID, '_blank');
    }
  }
}
